import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  loading: false,
  error: null,
  tasks: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
  },
  selectedTask: null,
  updating: false,
  deleting: false,
  updatingStatus: false,
  filters: {
    project_id: null,
    assigned_to: null,
  },
  employeeTaskDateRanges: [],
  employeeTaskDateRangesLoading: false,
  topPriorityTasks: [],
  topPriorityTasksLoading: false,
  topPriorityTasksError: null,
};

const taskSlice = createSlice({
  name: "companyTask",
  initialState,
  reducers: {
    // Get Tasks
    getTasksStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getTasksSuccess: (state, action) => {
      state.loading = false;
      state.tasks = action.payload.tasks || action.payload || [];
      if (action.payload.pagination) {
        state.pagination = action.payload.pagination;
      }
      state.error = null;
    },
    getTasksFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create Task
    createTaskStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    createTaskSuccess: (state, action) => {
      state.loading = false;
      state.tasks.push(action.payload);
      state.error = null;
    },
    createTaskFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update Task
    updateTaskStart: (state) => {
      state.updating = true;
      state.error = null;
    },
    updateTaskSuccess: (state, action) => {
      state.updating = false;
      const index = state.tasks.findIndex(
        (task) => task.id === action.payload.id,
      );
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      state.error = null;
    },
    updateTaskFailure: (state, action) => {
      state.updating = false;
      state.error = action.payload;
    },

    // Update Task Status
    updateTaskStatusStart: (state) => {
      state.updatingStatus = true;
      state.error = null;
    },
    updateTaskStatusOptimistic: (state, action) => {
      const { taskId, status } = action.payload;
      const index = state.tasks.findIndex(
        (task) => String(task.id) === String(taskId),
      );
      if (index !== -1) {
        state.tasks[index] = {
          ...state.tasks[index],
          status: status,
        };
      }
      const topIndex = state.topPriorityTasks.findIndex(
        (task) => String(task.id) === String(taskId),
      );
      if (topIndex !== -1) {
        state.topPriorityTasks[topIndex] = {
          ...state.topPriorityTasks[topIndex],
          status: status,
        };
      }
    },
    rollbackTaskStatus: (state, action) => {
      const { taskId, previousStatus } = action.payload;
      const index = state.tasks.findIndex(
        (task) => String(task.id) === String(taskId),
      );
      if (index !== -1) {
        state.tasks[index] = {
          ...state.tasks[index],
          status: previousStatus,
        };
      }
      const topIndex = state.topPriorityTasks.findIndex(
        (task) => String(task.id) === String(taskId),
      );
      if (topIndex !== -1) {
        state.topPriorityTasks[topIndex] = {
          ...state.topPriorityTasks[topIndex],
          status: previousStatus,
        };
      }
    },
    updateTaskStatusSuccess: (state, action) => {
      state.updatingStatus = false;
      const targetId = action.payload?.id || action.payload?.task_id;
      if (action.payload && targetId) {
        const index = state.tasks.findIndex(
          (task) => String(task.id) === String(targetId),
        );
        if (index !== -1) {
          state.tasks[index] = {
            ...state.tasks[index],
            ...action.payload,
          };
        }
        const topIndex = state.topPriorityTasks.findIndex(
          (task) => String(task.id) === String(targetId),
        );
        if (topIndex !== -1) {
          state.topPriorityTasks[topIndex] = {
            ...state.topPriorityTasks[topIndex],
            ...action.payload,
          };
        }
      }
      state.error = null;
    },
    updateTaskStatusFailure: (state, action) => {
      state.updatingStatus = false;
      state.error = action.payload;
    },

    // Delete Task
    deleteTaskStart: (state) => {
      state.deleting = true;
      state.error = null;
    },
    deleteTaskSuccess: (state, action) => {
      state.deleting = false;
      state.tasks = state.tasks.filter((task) => task.id !== action.payload);
      state.error = null;
    },
    deleteTaskFailure: (state, action) => {
      state.deleting = false;
      state.error = action.payload;
    },

    // Set Selected Task
    setSelectedTask: (state, action) => {
      state.selectedTask = action.payload;
    },

    // Set Filters
    setTaskFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Clear Filters
    clearTaskFilters: (state) => {
      state.filters = {
        project_id: null,
        assigned_to: null,
      };
    },

    // Clear Error
    clearError: (state) => {
      state.error = null;
    },
    getEmployeeTaskDateRangesStart: (state) => {
      state.employeeTaskDateRangesLoading = true;
    },

    getEmployeeTaskDateRangesSuccess: (state, action) => {
      state.employeeTaskDateRangesLoading = false;
      state.employeeTaskDateRanges = action.payload || [];
    },

    getEmployeeTaskDateRangesFailure: (state) => {
      state.employeeTaskDateRangesLoading = false;
    },

    // Get Top Priority Tasks
    getTopPriorityTasksStart: (state) => {
      state.topPriorityTasksLoading = true;
      state.topPriorityTasksError = null;
    },
    getTopPriorityTasksSuccess: (state, action) => {
      state.topPriorityTasksLoading = false;
      state.topPriorityTasks = action.payload || [];
      state.topPriorityTasksError = null;
    },
    getTopPriorityTasksFailure: (state, action) => {
      state.topPriorityTasksLoading = false;
      state.topPriorityTasksError = action.payload;
    },
  },
});

export const {
  getTasksStart,
  getTasksSuccess,
  getTasksFailure,
  createTaskStart,
  createTaskSuccess,
  createTaskFailure,
  updateTaskStart,
  updateTaskSuccess,
  updateTaskFailure,
  updateTaskStatusStart,
  updateTaskStatusOptimistic,
  rollbackTaskStatus,
  updateTaskStatusSuccess,
  updateTaskStatusFailure,
  deleteTaskStart,
  deleteTaskSuccess,
  deleteTaskFailure,
  setSelectedTask,
  setTaskFilters,
  clearTaskFilters,
  clearError,
  getEmployeeTaskDateRangesStart,
  getEmployeeTaskDateRangesSuccess,
  getEmployeeTaskDateRangesFailure,
  getTopPriorityTasksStart,
  getTopPriorityTasksSuccess,
  getTopPriorityTasksFailure,
} = taskSlice.actions;

export default taskSlice.reducer;
